'use client'

import DatoLink from '@/components/DatoLink/DatoLink'
import { DatoLinkFragment } from '@/components/DatoLink/DatoLink.generated'
import { HeaderQuery } from '@/components/Header/Header.generated'
import { LocalizedLink } from '@/components/LocalizedLink'
import {
	DropdownSectionRecord,
	InternalLinkRecord,
	ResponsiveImage,
	StoryRecord,
} from '@/graphql/types'
import getUrlPrefix, { urlsPrefix } from '@/utils/getUrlPrefix'
import { transformLocale } from '@/utils/language'
import {
	cn,
	getGridCols,
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from '@desoutter/uikit'
import { Button } from '@desoutter/uikit/src/components/atoms/Button'
import { usePathname } from 'next/navigation'
import { ReactNode, useMemo, useState } from 'react'
import { DropdownCardMenuContent } from './DropdownMenu/DropdownCardMenuContent'
import { DropdownLinkMenuContent } from './DropdownMenu/DropdownLinkMenu'

export function HeaderMenu({
	menu,
	language,
}: {
	menu: HeaderQuery['header']['menu']
	language: string
}) {
	const pathname = usePathname()
	const [value, setValue] = useState<string | null>(null)

	// Transform language from DatoCMS format to URL format for pathname comparison
	const urlLanguage = transformLocale({
		locale: language,
		to: 'params',
	})

	const activeId = useMemo(() => {
		const pathnamePrefixes = new Set(
			menu.flatMap((menuItem) => {
				const prefixes = []
				if (menuItem.simpleLink?.id) {
					prefixes.push({
						id: menuItem.id,
						url: getUrlPrefix(menuItem.simpleLink as InternalLinkRecord),
					})
					/** 💩 Quick and dirty fix to keep the blog menu item underlined while user is in /articles */
					if (menuItem.label === 'Blog') {
						prefixes.push({
							id: menuItem.id,
							url: '/articles',
						})
					}
					/** 💩 End of quick fix */
				}

				if (menuItem.blocks?.length) {
					/** 💩 Quick and dirty fix to keep the Company menu item underlined while user is in /sustainability */
					if (menuItem.label === 'Company') {
						prefixes.push({
							id: menuItem.id,
							url: '/sustainability',
						})
					}
					/** 💩 End of quick fix */

					menuItem.blocks.forEach((block) => {
						switch (block.__typename) {
							case 'DropdownBlockRecord':
								if (block.titleLink.__typename === 'InternalLinkRecord') {
									prefixes.push({
										id: menuItem.id,
										url: urlsPrefix[block.titleLink.link.__typename],
									})
								}
								if (block.links) {
									block.links.forEach((link) => {
										if (link && link.__typename === 'InternalLinkRecord') {
											prefixes.push({
												id: menuItem.id,
												url: urlsPrefix[link.link.__typename],
											})
										}
									})
								}

								if (block.linkSections) {
									block.linkSections.forEach((section) => {
										section.links.forEach((link) => {
											if (link && link.__typename === 'InternalLinkRecord') {
												prefixes.push({
													id: menuItem.id,
													url: urlsPrefix[link.__typename],
												})
											}
										})
									})
								}
								break
							case 'DropdownCardBlockRecord':
								break
							default:
								break
						}
					})
				}

				if (menuItem.stories?.length) {
					menuItem.stories.forEach((story) => {
						if (story.link && story.link.__typename === 'InternalLinkRecord') {
							prefixes.push({
								id: menuItem.id,
								url: urlsPrefix[story.link.__typename],
							})
						}
					})
				}

				return prefixes
			}),
		)

		let BreakException = {}
		let activeId: string | undefined
		try {
			pathnamePrefixes.forEach((prefix) => {
				if (pathname.startsWith(`/${urlLanguage}${prefix.url}`)) {
					activeId = prefix.id
					// throw BreakException
				}
			})
		} catch (e) {
			if (e !== BreakException) throw e
		}

		return activeId
	}, [pathname, urlLanguage])

	return (
		<NavigationMenu
			className="hidden lg:flex"
			onValueChange={setValue}
			value={value}
		>
			<NavigationMenuList className="space-x-0 xl:gap-4">
				{menu.map((menuItem, index) =>
					menuItem.simpleLink?.id ? (
						<NavigationMenuLink
							key={index}
							aria-current={activeId === menuItem.id}
							className="group inline-flex w-max items-center justify-center rounded-md px-2 xl:px-4 py-2 text-sm xl:text-base font-medium transition-colors hover:bg-background/50 disabled:pointer-events-none disabled:opacity-5 aria-[current=true]:nav-active"
							asChild
						>
							<LocalizedLink
								locale={urlLanguage}
								href={getUrlPrefix(menuItem.simpleLink as InternalLinkRecord)}
							>
								{menuItem.label}
							</LocalizedLink>
						</NavigationMenuLink>
					) : (
						<NavigationMenuItem key={index}>
							<NavigationMenuTrigger
								aria-current={activeId === menuItem.id}
								className={cn(
									'text-sm px-2 xl:px-4 xl:text-base aria-[current=true]:nav-active',
								)}
							>
								{menuItem.label}
							</NavigationMenuTrigger>
							<NavigationMenuContent>
								<DropdownMenuContentWrapper
									colsNumber={menuItem.blocks.length}
									extraButtons={menuItem.extraButtons as InternalLinkRecord[]}
									stories={menuItem.stories as StoryRecord[]}
									closeMenu={() => setValue(null)}
								>
									{menuItem.blocks.map((block) => {
										switch (block.__typename) {
											case 'DropdownBlockRecord':
												return (
													<DropdownLinkMenuContent
														title={block.title}
														description={block.description}
														image={
															block.image?.responsiveImage as ResponsiveImage
														}
														titleLink={block.titleLink as InternalLinkRecord}
														links={block.links as InternalLinkRecord[]}
														linkSections={
															block.linkSections as DropdownSectionRecord[]
														}
														colsNumber={menuItem.blocks.length}
														showIcon={block.showIcons}
														showLinkDescriptions={block.showLinkDescriptions}
														key={block.id}
														closeMenu={() => setValue(null)}
													/>
												)
											case 'DropdownCardBlockRecord':
												return (
													<DropdownCardMenuContent
														dropdownCarMenu={block}
														colsNumber={menuItem.blocks.length}
														key={block.id}
														closeMenu={() => setValue(null)}
													/>
												)
											default:
												return null
										}
									})}
								</DropdownMenuContentWrapper>
							</NavigationMenuContent>
						</NavigationMenuItem>
					),
				)}
			</NavigationMenuList>
		</NavigationMenu>
	)
}

export function DropdownMenuContentWrapper({
	children,
	colsNumber,
	extraButtons,
	stories,
	closeMenu,
}: {
	children: ReactNode
	colsNumber: number
	extraButtons?: InternalLinkRecord[]
	stories?: StoryRecord[]
	closeMenu?: () => void
}) {
	const extraContent = extraButtons?.length || stories?.length
	return (
		<div className="w-[96vw] p-6 z-10 pb-8">
			<div
				className={cn(
					'grid gap-6',
					getGridCols(colsNumber + (extraContent ? 1 : 0)),
				)}
			>
				{children}
				{!!extraContent && (
					<div className="flex flex-col justify-start gap-4">
						{!!stories.length &&
							stories.map((story) => (
								<div
									key={story.title}
									onClick={closeMenu}
									className="w-full max-h-[40%] flex-grow p-4 mb-2 rounded-xl bg-no-repeat font-normal text-white"
									style={{
										backgroundImage: `url(${story.background.responsiveImage.src})`,
										backgroundSize: 'cover',
									}}
								>
									<DatoLink fragment={story.link as DatoLinkFragment}>
										<p className="mb-2 text-lg font-medium">{story.title}</p>
										<p className="text-sm">{story.text}</p>
									</DatoLink>
								</div>
							))}

						{!!extraButtons.length &&
							extraButtons.map((button, i) => (
								<div key={button.label} onClick={closeMenu}>
									<DatoLink fragment={button as DatoLinkFragment}>
										<Button
											className="w-full justify-center"
											color={i % 2 ? 'primary' : 'tertiary'}
											title={button.label}
										>
											{button.label}
										</Button>
									</DatoLink>
								</div>
							))}
					</div>
				)}
			</div>
		</div>
	)
}
